import React from "react";
import {
  Phone,
  Search,
  RefreshCw,
  Loader2,
  CheckCircle2,
  Clock,
  AlertCircle,
} from "lucide-react";
import {
  listReviewsAdmin,
  setReviewStatus,
  parseCallbackReview,
  isCallbackPending,
  notifyCallbacksChanged,
  CALLBACK_CONTACTED_NOTE,
} from "../../api/reviews.js";

function StatusPill({ pending }) {
  const base = "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium";
  if (!pending) {
    return (
      <span className={`${base} bg-emerald-100 text-emerald-700`}>
        <CheckCircle2 className="h-3.5 w-3.5" />
        Contacted
      </span>
    );
  }
  return (
    <span className={`${base} bg-amber-100 text-amber-800`}>
      <Clock className="h-3.5 w-3.5" />
      Pending
    </span>
  );
}

export default function RequestedCallbacksPage() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [busyId, setBusyId] = React.useState(null);
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("Pending");

  const load = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const listRes = await listReviewsAdmin({ page: 1, page_size: 200, search: search.trim() || undefined });
      const all = Array.isArray(listRes.data) ? listRes.data : [];
      setRows(all.filter((item) => parseCallbackReview(item).isCallback));
    } catch (err) {
      setError(err?.message || "Failed to load callback requests");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [search]);

  React.useEffect(() => {
    load();
  }, [load]);

  const filtered = React.useMemo(() => {
    return rows.filter((item) => {
      const pending = isCallbackPending(item);
      if (statusFilter === "Pending" && !pending) return false;
      if (statusFilter === "Contacted" && pending) return false;
      return true;
    });
  }, [rows, statusFilter]);

  const pendingCount = React.useMemo(
    () => rows.filter((item) => isCallbackPending(item)).length,
    [rows]
  );

  const updateStatus = async (review, contacted) => {
    const id = review.name || review.id;
    if (!id) return;
    setBusyId(id);
    setNotice("");
    const admin_response = contacted ? CALLBACK_CONTACTED_NOTE : "";
    const status = review.status || review.review_status || "Pending";
    try {
      await setReviewStatus({ review: id, status, admin_response });
      setRows((prev) =>
        prev.map((item) =>
          (item.name || item.id) === id ? { ...item, status, review_status: status, admin_response } : item
        )
      );
      notifyCallbacksChanged();
      setNotice(contacted ? "Marked as contacted" : "Reopened as pending");
      setTimeout(() => setNotice(""), 2500);
    } catch (err) {
      setError(err?.message || "Failed to update status");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4">
      {notice && (
        <div className="fixed top-4 right-4 z-50 px-4 py-3 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-800 text-xs font-medium shadow-lg flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4" />
          {notice}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-2xl bg-sky-50 flex items-center justify-center">
            <Phone className="h-5 w-5 text-sky-600" />
          </div>
          <div>
            <h1 className="text-sm md:text-base font-semibold text-slate-900">Requested Callbacks</h1>
            <p className="text-[11px] text-slate-500">
              Name, phone number and product IDs from shortlist callback requests
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-slate-200 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 max-w-md">
        <button
          type="button"
          onClick={() => setStatusFilter("Pending")}
          className={`rounded-2xl border p-4 text-left transition ${
            statusFilter === "Pending" ? "border-sky-300 bg-sky-50" : "border-slate-200 bg-white hover:border-sky-200"
          }`}
        >
          <div className="text-[11px] text-slate-500 font-medium">Pending</div>
          <div className="mt-1 text-2xl font-bold text-slate-900">{pendingCount}</div>
        </button>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-[11px] text-slate-500 font-medium">Total requests</div>
          <div className="mt-1 text-2xl font-bold text-slate-900">{rows.length}</div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 px-4 py-3 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 bg-slate-50 flex-1 min-w-[180px]">
          <Search className="h-4 w-4 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, phone or product ID..."
            className="bg-transparent text-xs outline-none w-full"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-1.5 rounded-xl border border-slate-200 text-xs bg-white outline-none"
        >
          <option value="Pending">Pending</option>
          <option value="Contacted">Contacted</option>
          <option value="All">All</option>
        </select>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-800 flex gap-2">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs text-slate-500">
            <tr>
              <th className="px-4 py-3 text-left font-medium">Submitted</th>
              <th className="px-4 py-3 text-left font-medium">Name</th>
              <th className="px-4 py-3 text-left font-medium">Phone</th>
              <th className="px-4 py-3 text-left font-medium">Product ID</th>
              <th className="px-4 py-3 text-left font-medium">Category</th>
              <th className="px-4 py-3 text-left font-medium">Status</th>
              <th className="px-4 py-3 text-left font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                  <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
                  Loading callbacks...
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                  No requested callbacks found.
                </td>
              </tr>
            ) : (
              filtered.map((item) => {
                const id = item.name || item.id;
                const busy = busyId === id;
                const details = parseCallbackReview(item);
                const pending = isCallbackPending(item);
                return (
                  <tr key={id} className="border-b border-slate-100 last:border-0 align-top">
                    <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">
                      {item.submitted_on ? String(item.submitted_on).slice(0, 16) : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-slate-900 font-medium text-xs">
                        {details.name || item.reviewer_name || "Anonymous"}
                      </div>
                      {details.email ? (
                        <div className="text-[11px] text-slate-500">{details.email}</div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-700">
                      {details.phone ? (
                        <a
                          href={`tel:${details.phone}`}
                          className="inline-flex items-center gap-1 font-medium text-sky-700 hover:underline"
                        >
                          <Phone className="h-3.5 w-3.5" />
                          {details.phone}
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-slate-800 break-all">
                      {details.productIds || item.product || "—"}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-700">
                      {item.product_category || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <StatusPill pending={pending} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1.5">
                        {pending ? (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => updateStatus(item, true)}
                            className="text-[11px] font-semibold text-emerald-700 hover:underline disabled:opacity-50"
                          >
                            Mark contacted
                          </button>
                        ) : (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => updateStatus(item, false)}
                            className="text-[11px] font-semibold text-slate-600 hover:underline disabled:opacity-50"
                          >
                            Reopen
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
